import React, { useMemo, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import PageHero from "../components/PageHero";
import { sermons, siteMeta } from "../data/siteContent";

const filterOptions = (items, key) => ["All", ...new Set(items.map((item) => item[key]))];

function SermonsPage() {
  const [filters, setFilters] = useState({
    search: "",
    book: "All",
    series: "All",
    speaker: "All",
    topic: "All",
    year: "All",
  });

  const filteredSermons = useMemo(() => {
    return sermons.filter((sermon) => {
      const query = filters.search.toLowerCase();
      const matchesSearch =
        !query ||
        sermon.title.toLowerCase().includes(query) ||
        sermon.passage.toLowerCase().includes(query) ||
        sermon.description.toLowerCase().includes(query);

      return (
        matchesSearch &&
        (filters.book === "All" || sermon.book === filters.book) &&
        (filters.series === "All" || sermon.series === filters.series) &&
        (filters.speaker === "All" || sermon.speaker === filters.speaker) &&
        (filters.topic === "All" || sermon.topic === filters.topic) &&
        (filters.year === "All" || sermon.year === filters.year)
      );
    });
  }, [filters]);

  const [featured, ...latest] = filteredSermons;

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <>
      <PageHero
        eyebrow="Sermons"
        title="The Preaching of God's Word"
        intro="We believe God's Word should be faithfully explained, understood in its context, centered on Christ, and applied to the life of the church."
        metaTitle={`Sermons | ${siteMeta.churchName}`}
      />
      <section className="site-section">
        <Container>
          <div className="filters-panel">
            <input
              type="search"
              value={filters.search}
              onChange={(event) => updateFilter("search", event.target.value)}
              placeholder="Search sermons"
            />
            {[
              ["book", "Book of the Bible"],
              ["series", "Series"],
              ["speaker", "Speaker"],
              ["topic", "Topic"],
              ["year", "Year"],
            ].map(([key, label]) => (
              <select
                key={key}
                value={filters[key]}
                onChange={(event) => updateFilter(key, event.target.value)}
                aria-label={label}
              >
                {filterOptions(sermons, key).map((option) => (
                  <option key={option} value={option}>
                    {label}: {option}
                  </option>
                ))}
              </select>
            ))}
          </div>

          {featured ? (
            <>
              <div className="section-heading">
                <p className="section-kicker">Featured Sermon</p>
                <h2>{featured.title}</h2>
              </div>
              <div className="featured-sermon-card">
                <div className="featured-sermon-card__media">
                  <img src={featured.thumbnail} alt={featured.title} />
                </div>
                <div className="featured-sermon-card__content">
                  <p className="meta-line">
                    {featured.passage} / {featured.preacher} / {featured.date}
                  </p>
                  <p>{featured.description}</p>
                  <p className="notes-block">{featured.notes}</p>
                  <div className="button-row">
                    <a
                      href={featured.audioUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="site-button site-button--primary"
                    >
                      Audio
                    </a>
                    <a
                      href={featured.videoUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="site-button site-button--secondary"
                    >
                      Video
                    </a>
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(
                        `${featured.title} - ${featured.videoUrl}`
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                      className="site-button site-button--secondary"
                    >
                      Share
                    </a>
                  </div>
                </div>
              </div>

              <div className="section-heading section-heading--spaced">
                <p className="section-kicker">Latest Sermons</p>
                <h2>Recent preaching and Bible study</h2>
              </div>
              <Row className="g-4">
                {latest.map((sermon) => (
                  <Col md={6} xl={4} key={sermon.id}>
                    <article className="sermon-page-card h-100">
                      <img src={sermon.thumbnail} alt={sermon.title} loading="lazy" />
                      <div className="sermon-page-card__body">
                        <p className="meta-line">
                          {sermon.passage} / {sermon.preacher}
                        </p>
                        <h3>{sermon.title}</h3>
                        <p>{sermon.description}</p>
                        <div className="sermon-actions">
                          <a href={sermon.audioUrl} target="_blank" rel="noreferrer">
                            Listen
                          </a>
                          <a href={sermon.videoUrl} target="_blank" rel="noreferrer">
                            Watch
                          </a>
                          <button type="button">Notes</button>
                        </div>
                      </div>
                    </article>
                  </Col>
                ))}
              </Row>
            </>
          ) : (
            <div className="empty-state">
              <h2>No sermons found</h2>
              <p>Try changing one or more filters to see more sermons.</p>
            </div>
          )}
        </Container>
      </section>
    </>
  );
}

export default SermonsPage;
